import React, { Suspense, useEffect, useRef } from 'react'
import { Canvas } from '@react-three/fiber'
import { Audio } from 'expo-av'
import { OrbitControls, Stars, axesHelper, Html } from '@react-three/drei'
import { WorldEvents } from '../../systems/World'
import { useEvents } from '../../systems/World/hooks'
import CountDown from '../../components/CountDown'
import { Controllers, getPlayers } from '../../systems/HUD'
// import Rock from '../../components/Cube'
import Rock from '../../components/Arena'
import Player from '../../components/Player'
import Enemy from '../../components/Enemy'
import { Physics } from '../../systems/Physics'
import LEVELS from '../../constants/levels'
import { CHARACTER_EVENTS, GAME_EVENTS } from '../../systems/World/Events'
import Crystal from '../../components/Crystal'
import Wall from '../../components/Wall'
import RevertSpinArea from '../../components/RevertSpinArea'
import Portals from '../../components/Portals'
import {
  SIMPLE_HIT,
  GAME_OVER,
  GAME_WON,
  ENEMY_DOWN,
  SCENE_LOADED,
  OVERCHARGED,
  FROZEN,
  COILED,
  TELEPORTED,
  IMPULSE_ON,
  IMPULSE_OFF,
  GROWN,
} from '../../constants/webView_comm_messages'

const [player1, player2] = getPlayers(2)
let playersOnArena = [];
let playerRef;
const useLevels = levelId => {

  const [sounds, setSounds] = React.useState();

  async function loadSounds() {
    const sounds = {}
    const createSoundAsync = async asset => {
      const { sound } = await Audio.Sound.createAsync(asset)
      return sound
    }
    
    sounds.arena_completed = await createSoundAsync(require('../../../assets/sounds/music/arena_completed.mp3'))
    sounds.arena_won = await createSoundAsync(require('../../../assets/sounds/music/arena_won.mp3'))

    setSounds(sounds);
  }
  useEffect(() => {
    // should unload but i'm reloading page so 🤷‍♂️
  }, [sounds])

  useEffect(() => {
    loadSounds()
    return () => {
      playersOnArena = []
    }
  },[])
  const playSound = soundId => sounds[soundId].replayAsync()

  const CURRENT_LEVEL = LEVELS[levelId]
  const enemyRefs = CURRENT_LEVEL.enemies.map(() => useRef(null))
  const wallRefs = CURRENT_LEVEL.arena.walls.map(() => useRef())
  playerRef = useRef(null)
  playersOnArena = [playerRef, ...enemyRefs]
  const pos = useRef(CURRENT_LEVEL.enemies.length + 1)
  useEvents(CHARACTER_EVENTS.OUT_OF_RING, characterRef => {
    const isUserFall = characterRef === playerRef
    const playersAlive = playersOnArena.filter(pRef => pRef !== characterRef)
    const thereArePlayersLeft = playersAlive.length > 0
    const onlyOnePlayersLeft = playersAlive.length === 1
    const userIsAlive = playersAlive.find(pRef => pRef === playerRef) !== undefined

    if (!isUserFall && userIsAlive) {
      pos.current = pos.current - 1
      window.ReactNativeWebView?.postMessage(`${ENEMY_DOWN}:${pos.current}`);
    }
    if (thereArePlayersLeft && isUserFall) {
      playSound('arena_completed')
      setTimeout(() => {
        window.ReactNativeWebView?.postMessage(`${GAME_OVER}:${pos.current}`);
      }
      , 1000)
    }
    if (userIsAlive && onlyOnePlayersLeft) {
      playSound('arena_won')
      setTimeout(() => {
        window.ReactNativeWebView?.postMessage(GAME_WON);
      }
      , 1000)
    }
    playersOnArena = playersAlive;
  })
  // playersOnArena.push(...[playerRef, ...enemyRefs])
  return [
    <Player
      position={CURRENT_LEVEL.player.position}
      radius={0.5}
      userController={player2}
      color={CURRENT_LEVEL.player.color}
      ref={playerRef}
    />,
    CURRENT_LEVEL.enemies.map((enemy, idx) => 
      <Enemy
        key={enemy.id}
        position={enemy.position}
        radius={0.5}
        color={enemy.color}
        ref={enemyRefs[idx]}
        enemies={[playerRef]}
        model={enemy.model}
        spin={enemy.spin}
      />
    ),
    CURRENT_LEVEL.arena.walls.map((wall, idx) =>
      <Wall
        key={`wall_${idx}`}
        position={wall.position}
        color={wall.color}
        rotate={wall.rotate}
        hx={0.25}
        hy={3}
        ref={wallRefs[idx]}
      />
    ),
    CURRENT_LEVEL.crystals.map((crystal, idx) =>
      <Crystal
        key={`crystal_${idx}`}
        position={crystal.position}
        scale={[1.5, 1.5, 1.5]}
        type={crystal.type}
        timeToAppear={crystal.timeToAppear}
      />
    ),
    CURRENT_LEVEL.portalGroups?.map((portals, idx) =>
      <Portals
        key={`portals_${idx}`}
        positionBlue={portals[1].position}
        positionRed={portals[0].position}
      />
    ),
    CURRENT_LEVEL.spinReverseAreas?.map((area, idx) =>
      <RevertSpinArea
        key={`spin_area_${idx}`}
        position={area.position}
        hx={area.hx}
        hy={area.hy}
        rotate={area.rotate}
      />
    )
  ]
}

const GamePlay = ({ onLoad, currentLevel = 0, onEnd }) => {
  const [player, enemies, walls, crystals, portals, spinReverseAreas] = useLevels(currentLevel)
  const arena = LEVELS[currentLevel].arena

  const [sounds, setSounds] = React.useState();

  async function loadSounds() {
    const sounds = {}
    const createSoundAsync = async asset => {
      const { sound } = await Audio.Sound.createAsync(asset)
      return sound
    }
    
    sounds.simpleHit = await createSoundAsync(require('../../../assets/sounds/fx/ufoHit.mp3'))
    sounds.teleport = await createSoundAsync(require('../../../assets/sounds/fx/teleport.mp3'))
    sounds.coil_discharge = await createSoundAsync(require('../../../assets/sounds/fx/coil_discharge.mp3'))
    sounds.crystal_pick = await createSoundAsync(require('../../../assets/sounds/fx/crystal_pick.mp3'))
    sounds.grow = await createSoundAsync(require('../../../assets/sounds/fx/grow.mp3'))
    sounds.freezeEffect = await createSoundAsync(require('../../../assets/sounds/fx/freezeEffect.mp3'))
    sounds.gameplay = await createSoundAsync(require('../../../assets/sounds/music/gameplay.mp3'))

    setSounds(sounds);
  }
  useEffect(() => {
    // should unload but i'm reloading page so 🤷‍♂️
  }, [sounds]);

  useEffect(() => {
    loadSounds()
  }, [])

  const playSound = (soundId, props) => sounds[soundId].replayAsync(props)

  useEvents(CHARACTER_EVENTS.BEGIN_CONTACT, contact => {
    const fixtureA = contact.GetFixtureA()
    const fixtureB = contact.GetFixtureB()
    const dataA = fixtureA.GetBody().GetUserData()
    const dataB = fixtureB.GetBody().GetUserData()
    if ((dataA.isPlayer || dataA.isEnemy || dataA.isWall) && (dataB.isPlayer || dataB.isEnemy || dataB.isWall)) {
      // window.ReactNativeWebView?.postMessage(SIMPLE_HIT);
      playSound('simpleHit')
    }
  })
  useEvents(GAME_EVENTS.SYSTEM_ONLINE, () => {
    playSound('gameplay', { isLooping: true })
  })
  useEvents(CHARACTER_EVENTS.GETS_FREEZER, () => {
    playSound('freezeEffect')
  })
  useEvents(CHARACTER_EVENTS.GETS_COIL, () => {
    playSound('coil_discharge')
  })
  useEvents(CHARACTER_EVENTS.OVERCHARGE, () => {
    playSound('crystal_pick')
  })
  useEvents(CHARACTER_EVENTS.PICK_CRYSTAL_GROW, () => {
    playSound('grow')
  })
  useEvents(CHARACTER_EVENTS.TELEPORT, () => {
    playSound('teleport')
  })

  return (
    <>
      <WorldEvents>
        <Canvas
          style={{ background: "#171717"
            , flexBasis: '100%', width: 900, minHeight: 400, margin: 'auto'
          }}
          shadows
          // invalidateFrameloop
          // pixelRatio={Dimensions.get('window').height / Dimensions.get('window').width}

          // camera={{ position: [-5, 11, 20], fov: 30 }}
          camera={{ position: [-3, -15, 10], fov: 30 }}
          concurrent
          gl={{ antialias: true }}
          onCreated={({ gl, camera }) => {
            camera.lookAt(0, 0, 3)
          }}
        >
          <CountDown />
          <ambientLight intensity={1} color="#dadada"/>
        
          <pointLight position={[-5, 50, 0]} intensity={0.2} distance={10} color="lightblue" />
          
          <directionalLight
            castShadow
            position={[5, 8, -5]}
            intensity={0.5}
            // color="#ff9760"
            color="#ffffff"
            shadow-mapSize-width={600}
            shadow-mapSize-height={600}
            shadow-camera-far={50}
            shadow-camera-left={-10}
            shadow-camera-right={10}
            shadow-camera-top={10}
            shadow-camera-bottom={-10}
          />
          <Physics>
            {crystals}
            {enemies}
            {player}
            {walls}
            {portals}
            {spinReverseAreas}
          </Physics>
          <Rock
            tint={arena.tint}
            name={arena.name}
            scale={[16, 16, 16]}
            position={[0, -8.3, 0]}
            receiveShadow
          />
          <Stars/>
        </Canvas>
      </WorldEvents>
      <Controllers forPlayers={[player1, player2]} noUI={false}/>
    </>
  )
}

export default GamePlay
